import type { ReactNode } from 'react';

import type {
  NegociacaoAcordoItem,
  NegociacaoDocumentoUiItem,
  SecaoAcordo,
} from '../types/negociacaoDetailTypes';
import { extrairExtensao } from './negociacaoDetailUiUtils';

/**
 * Escolhe o único acordo cujo documento a aba Documentos mostra.
 *
 * Decisão de produto (Kleiton, CU-17tpepck66c): "deve mostrar sempre o contrato mais recente,
 * com o painel de assinaturas correspondente". Por isso a seção Acordo não lista todos os
 * contratos do caso: dois PDFs com o mesmo nome 'Acordo.pdf' ao lado de um painel só deixavam
 * o usuário sem saber qual documento o painel descreve.
 *
 * O acordo do painel vence porque é o par exato dos signatários exibidos. Sem painel (ou com um
 * id que não está na lista), vale o primeiro de `acordos`, que o core entrega ordenado por
 * `[createdAt desc, id desc]` — o mesmo desempate do acordo do painel, para que os dois GETs
 * concordem quando dois contratos nascem no mesmo instante.
 */
export function selecionarAcordoExibido(
  acordos: readonly NegociacaoAcordoItem[],
  acordoIdDoPainel: string | null,
): NegociacaoAcordoItem | null {
  return acordos.find((acordo) => acordo.id === acordoIdDoPainel) ?? acordos[0] ?? null;
}

export function montarDocumentosDoAcordo(
  acordo: NegociacaoAcordoItem | null,
  icone: ReactNode,
): NegociacaoDocumentoUiItem[] {
  if (!acordo?.docUrl) return [];

  const ext = extrairExtensao(acordo.docUrl);

  return [
    {
      id: acordo.id,
      name: ext ? `Acordo.${ext}` : `Acordo - ${acordo.id.slice(0, 8)}`,
      createdAt: acordo.createdAt,
      icon: icone,
      url: acordo.docUrl,
      downloadUrl: acordo.docDownloadUrl ?? null,
      type: 'DOCUMENT',
      origem: 'ESCRITORIO',
    },
  ];
}

/**
 * Monta a seção Acordo da aba Documentos só com o acordo de `selecionarAcordoExibido`: a linha
 * do documento dele e o id que `montarSecoesDocumentos` usa para separá-la dos demais arquivos.
 */
export function montarSecaoAcordo(
  acordos: readonly NegociacaoAcordoItem[],
  acordoIdDoPainel: string | null,
  icone: ReactNode,
): SecaoAcordo {
  const acordoExibido = selecionarAcordoExibido(acordos, acordoIdDoPainel);

  return {
    documentos: montarDocumentosDoAcordo(acordoExibido, icone),
    acordoIds: acordoExibido ? [acordoExibido.id] : [],
  };
}
